import type { AppResource, AssetType, BulkRunCellState, BulkRunState, CostSummaryResource, Target } from '@studio/shared';
import { targetKey } from '../app/history.js';

/**
 * The words and numbers a Bulk Run is read in.
 *
 * There is deliberately nothing here that turns progress into a finishing time: the
 * queue depth, the provider and the machine all decide how long a run takes, and an
 * estimate the Studio cannot keep is worse than no estimate at all.
 */

export function money(usd: number): string {
  return `$${usd.toFixed(2)}`;
}

/** The range as the preflight says it. One dash, two ends, no average in between. */
export function rangeLine(lowUsd: number, highUsd: number): string {
  return lowUsd === highUsd ? money(highUsd) : `${money(lowUsd)}–${money(highUsd)}`;
}

export function runStateLabel(state: BulkRunState): string {
  switch (state) {
    case 'running':
      return 'Выполняется';
    case 'paused':
      return 'На паузе';
    case 'ceilingReached':
      return 'Достигнут потолок';
    case 'stopped':
      return 'Остановлено';
    case 'completed':
      return 'Завершено';
  }
}

export function cellStateLabel(state: BulkRunCellState): string {
  switch (state) {
    case 'queued':
      return 'В очереди';
    case 'working':
      return 'В работе';
    case 'ready':
      return 'Готово';
    case 'needsAttention':
      return 'Нужно внимание';
    case 'stopped':
      return 'Остановлено';
  }
}

/** Ready out of the whole matrix. Counting, not predicting. */
export function progressLine(readyCount: number, cellCount: number): string {
  return `${readyCount} из ${cellCount} готово`;
}

export function spendLine(spentUsd: number, ceilingUsd: number | null): string {
  return ceilingUsd === null ? `${money(spentUsd)} · Без лимита` : `${money(spentUsd)} / ${money(ceilingUsd)}`;
}

/**
 * Compact spend: what the work was estimated at and what the providers reported
 * charging. Two numbers, and how many Attempts they are made of - no tokens, no
 * balance, no invoice. Subscription Attempts are named beside them, never as $0.
 */
export function spentOverPeriod(costs: CostSummaryResource): string {
  const priced = `Оценка ${money(costs.estimatedUsd)} · факт ${money(costs.actualUsd)} по ${costs.attemptCount} Generation Attempts`;
  const subscription = `${costs.subscriptionAttemptCount} по подписке, стоимость не сообщается`;
  if (costs.subscriptionAttemptCount === 0) return priced;
  if (costs.attemptCount === 0) return `${costs.subscriptionAttemptCount} Generation Attempts по подписке · стоимость не сообщается`;
  // The dollars are then only part of the spend, and say so.
  return `Известные траты: ${priced} · ${subscription}`;
}

export interface MatrixRow {
  readonly appId: string;
  readonly target: Target;
  readonly assetType: AssetType;
}

/**
 * The matrix, expanded: Apps × Targets × Asset Types, in the order they were chosen,
 * so what the operator ticks is what they see listed.
 *
 * Choosing no Target at all is not an empty matrix - it is each App tested where that
 * App already says it is tested, which is what its own default Target means. Resolving
 * that here rather than on the server is what lets a row keep the same identity in the
 * browser as it has in the preflight that comes back.
 */
export function expandMatrix(
  apps: readonly AppResource[],
  appIds: readonly string[],
  targets: readonly Target[],
  assetTypes: readonly AssetType[],
): readonly MatrixRow[] {
  return appIds.flatMap((appId) => {
    const app = apps.find((entry) => entry.id === appId);
    if (app === undefined) return [];
    const wanted = targets.length === 0 ? [app.defaultTarget] : targets;
    return wanted.flatMap((target) => assetTypes.map((assetType) => ({ appId, target, assetType })));
  });
}

/**
 * The key a matrix cell is identified by in the browser, before any run exists.
 *
 * Coordinates and nothing else. A preflight that comes back names the Experiment each
 * row created, and folding that in here made a row read one way on the way out and
 * another on the way back - which is how `Run again` lost the row exception it was
 * restoring. What an export-preparation row adds to this is added where that mode is
 * known, not by sniffing a field that is populated for other reasons too.
 */
export function rowKey(row: { readonly appId: string; readonly target: Target; readonly assetType: AssetType }): string {
  return `${row.appId}|${targetKey(row.target)}|${row.assetType}`;
}
